<template>
  <div class="course-analysis-container">
    <!-- 分析头部 -->
    <div class="analysis-header">
      <h3><i class="el-icon-data-analysis"></i> 课程学情分析</h3>
      <div class="analysis-actions">
        <el-button type="primary" size="small" @click="generateAnalysis" :loading="generating">
          <i class="el-icon-refresh"></i> 重新分析
        </el-button>
        <el-button size="small" @click="refreshAnalysis" :loading="loading">
          <i class="el-icon-refresh"></i> 刷新数据
        </el-button>
      </div>
    </div>

    <!-- 分析内容 -->
    <div v-if="analysisData" class="analysis-content">
      <!-- 总体统计卡片 -->
      <el-row :gutter="20" class="stats-row">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ analysisData.totalStudents }}</div>
              <div class="stat-label">总学生数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ analysisData.activeStudents }}</div>
              <div class="stat-label">活跃学生</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-trophy"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ analysisData.averageScore ? analysisData.averageScore.toFixed(1) : '0.0' }}</div>
              <div class="stat-label">平均分</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-success"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ analysisData.passRate ? analysisData.passRate.toFixed(1) : '0.0' }}%</div>
              <div class="stat-label">及格率</div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 详细统计 -->
      <el-row :gutter="20" class="detailed-stats-row">
        <el-col :span="12">
          <el-card class="chart-card">
            <div slot="header">
              <span>成绩分布</span>
            </div>
            <div ref="scoreDistributionChart" style="height: 300px;"></div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card class="chart-card">
            <div slot="header">
              <span>学习进度统计</span>
            </div>
            <div ref="progressChart" style="height: 300px;"></div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 学生成绩列表 -->
      <el-card class="student-scores-card">
        <div slot="header">
          <span>学生成绩详情</span>
          <el-button style="float: right; padding: 3px 0" type="text" @click="exportStudentScores">
            <i class="el-icon-download"></i> 导出
          </el-button>
        </div>
        <el-table :data="analysisData.studentScores" border stripe>
          <el-table-column prop="studentName" label="学生姓名" width="120" />
          <el-table-column prop="averageScore" label="平均分" width="100" align="center">
            <template slot-scope="scope">
              <span :class="getScoreClass(scope.row.averageScore)">
                {{ scope.row.averageScore ? scope.row.averageScore.toFixed(1) : '0.0' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="作业完成情况" width="150" align="center">
            <template slot-scope="scope">
              <el-progress
                :percentage="Math.min(100, Math.max(0, Math.round((scope.row.completedHomework || 0) / (scope.row.totalHomework || 1) * 100)))"
                :color="getProgressColor(scope.row.completedHomework, scope.row.totalHomework)"
                :show-text="false"
              />
              <span style="font-size: 12px; color: #666;">
                {{ scope.row.completedHomework }}/{{ scope.row.totalHomework }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="examCount" label="考试次数" width="100" align="center" />
          <el-table-column prop="studyStatus" label="学习状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.studyStatus)" size="small">
                {{ scope.row.studyStatus }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" align="center">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" @click="viewStudentDetail(scope.row)">
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 知识点掌握情况 -->
      <el-card class="knowledge-card">
        <div slot="header">
          <span>知识点掌握情况</span>
        </div>
        <el-table :data="analysisData.knowledgePoints" border stripe>
          <el-table-column prop="knowledgeName" label="知识点" width="150" />
          <el-table-column label="掌握程度" width="200">
            <template slot-scope="scope">
              <el-progress
                :percentage="Math.min(100, Math.max(0, Math.round(scope.row.masteryLevel || 0)))"
                :color="getMasteryColor(scope.row.masteryLevel)"
                :show-text="false"
              />
              <span style="font-size: 12px; color: #666;">
                {{ scope.row.masteryLevel ? scope.row.masteryLevel.toFixed(1) : '0.0' }}%
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="questionCount" label="相关题目数" width="120" align="center" />
          <el-table-column prop="correctRate" label="正确率" width="120" align="center">
            <template slot-scope="scope">
              <span :class="getCorrectRateClass(scope.row.correctRate)">
                {{ scope.row.correctRate ? scope.row.correctRate.toFixed(1) : '0.0' }}%
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 学习建议 -->
      <el-card class="suggestions-card">
        <div slot="header">
          <span>学习建议</span>
        </div>
        <ul class="suggestions-list">
          <li v-for="(suggestion, index) in analysisData.suggestions" :key="index">
            <i class="el-icon-lightbulb"></i>
            {{ suggestion }}
          </li>
        </ul>
      </el-card>
    </div>

    <!-- 无数据状态 -->
    <el-card v-else class="empty-card">
      <div class="empty-content">
        <i class="el-icon-data-analysis empty-icon"></i>
        <h3>暂无学情分析数据</h3>
        <p>点击下方按钮生成课程学情分析报告</p>
        <el-button type="primary" @click="generateAnalysis" :loading="generating">
          <i class="el-icon-refresh"></i> 生成分析报告
        </el-button>
      </div>
    </el-card>

    <!-- 学生详情弹窗 -->
    <el-dialog title="学生详情" :visible.sync="studentDetailVisible" width="800px">
      <div v-if="selectedStudent" class="student-detail">
        <h4>{{ selectedStudent.studentName }} 的学习情况</h4>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="平均分">{{ selectedStudent.averageScore ? selectedStudent.averageScore.toFixed(1) : '0.0' }}</el-descriptions-item>
          <el-descriptions-item label="作业完成数">{{ selectedStudent.completedHomework }}/{{ selectedStudent.totalHomework }}</el-descriptions-item>
          <el-descriptions-item label="考试次数">{{ selectedStudent.examCount }}</el-descriptions-item>
          <el-descriptions-item label="学习状态">{{ selectedStudent.studyStatus }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseAnalysis, generateCourseAnalysis } from '@/api/courseAnalysis'
import * as echarts from 'echarts'

export default {
  name: 'CourseAnalysis',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      analysisData: null,
      loading: false,
      generating: false,
      studentDetailVisible: false,
      selectedStudent: null,
      scoreDistributionChart: null,
      progressChart: null
    }
  },
  mounted() {
    this.loadAnalysis()
  },
  beforeDestroy() {
    if (this.scoreDistributionChart) {
      this.scoreDistributionChart.dispose()
    }
    if (this.progressChart) {
      this.progressChart.dispose()
    }
  },
  methods: {
    // 加载学情分析数据
    async loadAnalysis() {
      this.loading = true
      try {
        const response = await getCourseAnalysis(this.courseId)
        if (response.code === 1) {
          this.analysisData = response.data
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$message.error(response.msg || '获取学情分析失败')
        }
      } catch (error) {
        console.error('获取学情分析失败:', error)
        this.$message.error('获取学情分析失败')
      } finally {
        this.loading = false
      }
    },

    // 生成学情分析
    async generateAnalysis() {
      this.generating = true
      try {
        const response = await generateCourseAnalysis(this.courseId)
        if (response.code === 1) {
          this.analysisData = response.data
          this.$message.success('学情分析生成成功')
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$message.error(response.msg || '生成学情分析失败')
        }
      } catch (error) {
        console.error('生成学情分析失败:', error)
        this.$message.error('生成学情分析失败')
      } finally {
        this.generating = false
      }
    },

    // 刷新分析数据
    refreshAnalysis() {
      this.loadAnalysis()
    },

    // 初始化图表
    initCharts() {
      this.initScoreDistributionChart()
      this.initProgressChart()
    },

    // 初始化成绩分布图表
    initScoreDistributionChart() {
      if (!this.$refs.scoreDistributionChart || !this.analysisData || !this.analysisData.scoreDistribution) return

      this.scoreDistributionChart = echarts.init(this.$refs.scoreDistributionChart)
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: '成绩分布',
            type: 'pie',
            radius: '50%',
            data: Object.entries(this.analysisData.scoreDistribution).map(([name, value]) => ({
              value,
              name
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      this.scoreDistributionChart.setOption(option)
    },

    // 初始化学习进度图表
    initProgressChart() {
      if (!this.$refs.progressChart || !this.analysisData || !this.analysisData.progressStats) return

      this.progressChart = echarts.init(this.$refs.progressChart)
      const option = {
        tooltip: {
          trigger: 'item'
        },
        series: [
          {
            name: '学习进度',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '18',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: Object.entries(this.analysisData.progressStats).map(([name, value]) => ({
              value,
              name
            }))
          }
        ]
      }
      this.progressChart.setOption(option)
    },

    // 获取分数样式类
    getScoreClass(score) {
      if (score >= 90) return 'score-excellent'
      if (score >= 80) return 'score-good'
      if (score >= 70) return 'score-medium'
      if (score >= 60) return 'score-pass'
      return 'score-fail'
    },

    // 获取进度条颜色
    getProgressColor(completed, total) {
      const percentage = (completed / total) * 100
      if (percentage >= 90) return '#67C23A'
      if (percentage >= 70) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取状态类型
    getStatusType(status) {
      const statusMap = {
        '优秀': 'success',
        '良好': 'success',
        '一般': 'warning',
        '需加强': 'danger'
      }
      return statusMap[status] || 'info'
    },

    // 获取掌握程度颜色
    getMasteryColor(level) {
      if (level >= 80) return '#67C23A'
      if (level >= 60) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取正确率样式类
    getCorrectRateClass(rate) {
      if (rate >= 80) return 'correct-rate-high'
      if (rate >= 60) return 'correct-rate-medium'
      return 'correct-rate-low'
    },

    // 查看学生详情
    viewStudentDetail(student) {
      this.selectedStudent = student
      this.studentDetailVisible = true
    },

    // 导出学生成绩
    exportStudentScores() {
      this.$message.info('导出功能开发中...')
    }
  }
}
</script>

<style scoped>
.course-analysis-container {
  padding: 20px;
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.analysis-header h3 {
  margin: 0;
  color: #409EFF;
  font-size: 18px;
}

.analysis-actions {
  display: flex;
  gap: 10px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 40px;
  color: #409EFF;
  margin-right: 15px;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.detailed-stats-row {
  margin-bottom: 20px;
}

.chart-card {
  height: 400px;
}

.student-scores-card,
.knowledge-card,
.suggestions-card {
  margin-bottom: 20px;
}

.empty-card {
  text-align: center;
  padding: 40px;
}

.empty-content {
  padding: 40px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-content h3 {
  color: #606266;
  margin-bottom: 10px;
}

.empty-content p {
  color: #909399;
  margin-bottom: 30px;
}

.suggestions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestions-list li {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #606266;
  line-height: 1.6;
}

.suggestions-list li:last-child {
  border-bottom: none;
}

.suggestions-list li i {
  color: #409EFF;
  margin-right: 8px;
}

.score-excellent { color: #67C23A; font-weight: bold; }
.score-good { color: #409EFF; font-weight: bold; }
.score-medium { color: #E6A23C; font-weight: bold; }
.score-pass { color: #909399; font-weight: bold; }
.score-fail { color: #F56C6C; font-weight: bold; }

.correct-rate-high { color: #67C23A; font-weight: bold; }
.correct-rate-medium { color: #E6A23C; font-weight: bold; }
.correct-rate-low { color: #F56C6C; font-weight: bold; }

.student-detail {
  padding: 20px 0;
}
</style>
